import { DateRangePreset } from "../types/reportTypes";

// Master set of date-range presets the application supports.
//
// This is the *definition* of what a preset key means (its label). It is NOT
// the list shown in any particular dropdown — which presets a given report
// offers is configured per report, in that report's Metadata.parameters[]
// entry (see resolveConfiguredPresets below).
//
// Every key here must also have a matching case in resolveDateRangePreset.ts,
// otherwise the preset renders but resolves to no date range. `as const` makes
// that contract checkable: DateRangePresetKey is derived from these keys, so a
// typo in a preset list is a compile error rather than a blank dropdown row.
//
// Note "allDates" is NOT the same key as Commission's older "includeAllDates"
// (see getDateFilter in src/utils/index.ts and redux/Commission/FilterSlice.ts).
// They mean different things — "allDates" sends the full ALL_DATES_START..
// ALL_DATES_END range, "includeAllDates" sends no date filter at all — so the
// two are not interchangeable.
export const DATE_RANGE_PRESET_LABELS = {
  custom: "Custom",
  thisWeek: "This Week",
  lastWeek: "Last Week",
  thisMonth: "This Month",
  lastMonth: "Last Month",
  last3Months: "Last 3 Months",
  last6Months: "Last 6 Months",
  last12Months: "Last 12 Months",
  thisCalendarYear: "This Calendar Year",
  lastCalendarYear: "Last Calendar Year",
  next30Days: "Next 30 Days",
  next60Days: "Next 60 Days",
  next90Days: "Next 90 Days",
  next3Months: "Next 3 Months",
  next6Months: "Next 6 Months",
  next12Months: "Next 12 Months",
  last30Days: "Last 30 Days",
  last60Days: "Last 60 Days",
  last90Days: "Last 90 Days",
  allDates: "Include all Dates",
} as const;

export type DateRangePresetKey = keyof typeof DATE_RANGE_PRESET_LABELS;

// Bounds the "Include all Dates" preset resolves to.
//
// The date pickers are hidden for this preset, so the range cannot come from
// the UI — and the reports API takes a start/end pair rather than treating
// missing dates as "everything", so an explicit range wide enough to cover any
// stored date is sent instead. Keeping the bounds here means the day the API
// gains a real "no date filter" mode, only resolveDateRangePreset needs to
// change.
export const ALL_DATES_START = "1900-01-01";
export const ALL_DATES_END = "2199-12-31";

export const isSupportedDatePreset = (key: string): key is DateRangePresetKey =>
  Object.prototype.hasOwnProperty.call(DATE_RANGE_PRESET_LABELS, key);

// Normalises a report's configured `presets` into the {label, value} pairs the
// DateRangePicker renders.
//
// Accepts either form so existing report definitions keep working:
//   - preset keys:      ["thisWeek", "lastMonth", "allDates"]
//   - full objects:     [{ label: "This Week", value: "thisWeek" }]
//   - a mix of the two
//
// A configured object's own label wins, so a report can relabel a preset
// ("Year to date") without touching this file. Order follows the report's
// configuration, giving the report author control over the dropdown order.
//
// This is the validation boundary for report metadata, which is server-provided
// and therefore untrusted: unsupported keys are dropped with a warning (in
// either form — an unknown key inside a {label, value} object would otherwise
// render an option that silently resolves to no dates), and repeats are
// collapsed so the rendered options stay unique.
export const resolveConfiguredPresets = (
  configured: Array<string | DateRangePreset> | undefined,
  fallback: DateRangePreset[]
): DateRangePreset[] => {
  if (!configured?.length) return fallback;

  const seen = new Set<string>();

  const resolved = configured.reduce<DateRangePreset[]>((acc, entry) => {
    const isObject = Boolean(entry) && typeof entry === "object";
    const value = isObject ? (entry as DateRangePreset).value : (entry as string);
    if (!value) return acc;

    if (!isSupportedDatePreset(value)) {
      console.warn(
        `Report configuration: unsupported date preset "${value}" — skipped. Supported presets: ${Object.keys(DATE_RANGE_PRESET_LABELS).join(", ")}`
      );
      return acc;
    }

    if (seen.has(value)) return acc;
    seen.add(value);

    acc.push({
      label:
        (isObject && (entry as DateRangePreset).label) ||
        DATE_RANGE_PRESET_LABELS[value],
      value,
    });
    return acc;
  }, []);

  // Every configured key was unusable — fall back rather than render an
  // empty dropdown the user cannot interact with.
  return resolved.length ? resolved : fallback;
};
